import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Input, Select, Space, Table, Typography, message } from 'antd';
import { API } from '@/services/api.service';
import { errorText } from '../format';

const { Text } = Typography;

/**
 * The map: which line of the form each account and organisational unit goes
 * to. The lines are those of the uploaded form.
 *
 * @param {Object} props - Component props
 * @param {string} props.companyId - Company
 * @param {Function} props.onSaved - Called after saving
 * @returns {JSX.Element} The settings form
 */
export default function ParentPlSettings({ companyId, onSaved }) {
  const { t } = useTranslation('clientReports');
  const queryClient = useQueryClient();
  const [form, setForm] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [search, setSearch] = useState('');

  const { data } = useQuery({
    queryKey: ['client-reports-parent-pl-config', companyId],
    queryFn: () => API.clientReports.getParentIncomeStatementConfig(companyId),
  });

  useEffect(() => {
    if (!data?.data) return;
    setForm({
      ...data.data,
      mapping: data.data.mapping.map((mp, i) => ({ ...mp, id: i })),
    });
    setDirty(false);
  }, [data]);

  const update = (patch) => {
    setForm((f) => ({ ...f, ...patch }));
    setDirty(true);
  };
  const updateMapping = (id, patch) =>
    update({ mapping: form.mapping.map((mp) => (mp.id === id ? { ...mp, ...patch } : mp)) });

  const saveMutation = useMutation({
    mutationFn: () =>
      API.clientReports.saveParentIncomeStatementConfig(companyId, {
        mapping: form.mapping.map(({ account, orgUnit, line }) => ({ account, orgUnit, line })),
      }),
    onSuccess: () => {
      setDirty(false);
      queryClient.invalidateQueries({ queryKey: ['client-reports-parent-pl-config', companyId] });
      message.success(t('parentPl.saved'));
      onSaved?.();
    },
    onError: async (e) => message.error(await errorText(e)),
  });

  const shown = useMemo(() => {
    if (!form) return [];
    const q = search.trim().toLowerCase();
    if (!q) return form.mapping;
    return form.mapping.filter((mp) =>
      [mp.account, mp.orgUnit, mp.line].some((v) => String(v || '').toLowerCase().includes(q))
    );
  }, [form, search]);

  if (!form) return null;

  const lineOptions = form.lines
    .filter((l) => l.kind === 'ledger')
    .map((l) => ({ value: l.key, label: l.label }));

  return (
    <div>
      <Text strong>{t('parentPl.fields.mapping')}</Text>
      <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 8 }}>
        {form.lines.length ? t('parentPl.hints.mapping') : t('parentPl.noTemplate')}
      </div>
      <Input.Search
        allowClear
        style={{ width: 320, marginBottom: 8 }}
        placeholder={t('parentPl.searchMapping')}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <Table
        size="small"
        rowKey="id"
        pagination={{ pageSize: 25, hideOnSinglePage: true, showSizeChanger: false }}
        dataSource={shown}
        style={{ marginBottom: 8 }}
        columns={[
          {
            title: t('parentPl.account'),
            dataIndex: 'account',
            key: 'account',
            width: 140,
            render: (account, mp) => (
              <Input value={account} onChange={(e) => updateMapping(mp.id, { account: e.target.value.trim() })} />
            ),
          },
          {
            title: t('parentPl.orgUnit'),
            dataIndex: 'orgUnit',
            key: 'orgUnit',
            width: 120,
            render: (orgUnit, mp) => (
              <Input
                value={orgUnit}
                placeholder="—"
                onChange={(e) => updateMapping(mp.id, { orgUnit: e.target.value.trim().toUpperCase() })}
              />
            ),
          },
          {
            title: t('parentPl.line'),
            dataIndex: 'line',
            key: 'line',
            render: (line, mp) => (
              <Select
                showSearch
                optionFilterProp="label"
                style={{ width: '100%' }}
                placeholder={t('parentPl.pickLine')}
                options={lineOptions}
                value={line || undefined}
                onChange={(value) => updateMapping(mp.id, { line: value })}
              />
            ),
          },
          {
            key: 'remove',
            width: 48,
            render: (_, mp) => (
              <Button
                type="text"
                shape="circle"
                danger
                icon={<DeleteOutlined />}
                aria-label={t('parentPl.removeMapping')}
                onClick={() => update({ mapping: form.mapping.filter((x) => x.id !== mp.id) })}
              />
            ),
          },
        ]}
      />
      <Space>
        <Button
          shape="round"
          icon={<PlusOutlined />}
          disabled={!form.lines.length}
          onClick={() => {
            setSearch('');
            update({
              mapping: [
                { id: Math.max(-1, ...form.mapping.map((mp) => mp.id)) + 1, account: '', orgUnit: '', line: '' },
                ...form.mapping,
              ],
            });
          }}
        >
          {t('parentPl.addMapping')}
        </Button>
        <Button
          type="primary"
          shape="round"
          className="btn-primary"
          disabled={!dirty}
          loading={saveMutation.isPending}
          onClick={() => saveMutation.mutate()}
        >
          {t('parentPl.save')}
        </Button>
        {dirty && <Text type="warning">{t('fccs.unsaved')}</Text>}
      </Space>
    </div>
  );
}
